import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  TextField,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  Collapse,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  getTournamentTeams,
  getRoundsMatches,
  saveTeamsRoundsMatches,
} from '../../../services/tournament.service';
import { MATCH_TIPO_RES } from '../../../constants/tournament.constants';
import { selectMenuProps, compactInputSx } from '../../../styles/wizardStyles';
import { parseMatchResult, serializeMatchResult } from '../../../utils/matchResult.utils';
import RoundsPanel from '../../../components/wizard/RoundsPanel';
import MatchResultSelect from '../../../components/wizard/MatchResultSelect';
import StepNavButtons from '../../../components/wizard/StepNavButtons';

export default function StepTeamRoundsMatches({
  torId,
  onNext,
  onBack,
  setAlertInfo,
  nroRondas,
  onUpdateTournamentRondas,
}) {
  const [teams, setTeams] = useState([]);
  const [rounds, setRounds] = useState([{ ron_id: 1, ron_numero: 1, ron_fecha: '' }]);
  const [matchups, setMatchups] = useState([]);
  const [matches, setMatches] = useState([]);
  const [activeRoundId, setActiveRoundId] = useState(1);
  const [selectedMatchupKey, setSelectedMatchupKey] = useState(null); // 'equ_1_id-equ_2_id'
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  // Initial states for dirty check
  const [initialRounds, setInitialRounds] = useState([]);
  const [initialMatchups, setInitialMatchups] = useState([]);
  const [initialMatches, setInitialMatches] = useState([]);

  // Form states for Matchup (pairing)
  const [selectedEqu1, setSelectedEqu1] = useState('');
  const [selectedEqu2, setSelectedEqu2] = useState('');

  // Form states for Board Match
  const [selectedBoard, setSelectedBoard] = useState(1);
  const [selectedWhite, setSelectedWhite] = useState('');
  const [selectedBlack, setSelectedBlack] = useState('');
  const [selectedResult, setSelectedResult] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true);
      try {
        const teamsData = await getTournamentTeams(torId);
        setTeams(teamsData || []);

        const data = await getRoundsMatches(torId);
        if (data && data.rondas && data.rondas.length > 0) {
          const formattedRounds = data.rondas.map((r) => ({
            ron_id: r.ron_id,
            ron_numero: r.ron_numero,
            ron_fecha: r.ron_fecha ? r.ron_fecha.split('T')[0] : '',
          }));
          setRounds(formattedRounds);

          const formattedMatchups = (data.enfrentamientos || []).map((e) => ({
            ron_id: e.ron_id,
            equ_1_id: e.equ_1_id,
            equ_2_id: e.equ_2_id,
            key: `${e.ron_id}-${e.equ_1_id}-${e.equ_2_id}`,
          }));
          setMatchups(formattedMatchups);

          const formattedMatches = (data.partidas || []).map((p, idx) => {
            const { result, isBye } = parseMatchResult(p);

            return {
              id: p.par_id || idx + 1,
              par_ron_id: p.par_ron_id,
              par_equ_1_id: p.par_equ_1_id,
              par_equ_2_id: p.par_equ_2_id,
              tablero: p.par_tablero || 1, // par_tablero used for board ordering/numbering here
              jug_blancas: p.par_jug_blancas,
              jug_negras: p.par_jug_negras || '',
              result: result,
            };
          });
          setMatches(formattedMatches);

          setInitialRounds(JSON.parse(JSON.stringify(formattedRounds)));
          setInitialMatchups(JSON.parse(JSON.stringify(formattedMatchups)));
          setInitialMatches(JSON.parse(JSON.stringify(formattedMatches)));

          setActiveRoundId(formattedRounds[0].ron_id);
          if (formattedMatchups.length > 0) {
            const firstInRound = formattedMatchups.find(m => m.ron_id === formattedRounds[0].ron_id);
            if (firstInRound) {
              setSelectedMatchupKey(firstInRound.key);
            }
          }
        } else {
          const defaultRounds = [{ ron_id: 1, ron_numero: 1, ron_fecha: '' }];
          setRounds(defaultRounds);
          setMatchups([]);
          setMatches([]);
          setInitialRounds(JSON.parse(JSON.stringify(defaultRounds)));
          setInitialMatchups([]);
          setInitialMatches([]);
          setActiveRoundId(1);
        }
      } catch (error) {
        console.error('Error fetching rounds and matches:', error);
        setAlertInfo({
          open: true,
          message: 'Error al obtener los datos de rondas y enfrentamientos.',
          severity: 'error',
        });
      } finally {
        setDataLoading(false);
      }
    };
    fetchData();
  }, [torId, setAlertInfo]);

  // Reset or adjust board number when selected matchup changes or matches are modified
  useEffect(() => {
    if (selectedMatchupKey) {
      const activeMatchup = matchups.find((m) => m.key === selectedMatchupKey);
      if (activeMatchup) {
        const activeMatchupMatches = matches.filter(
          (m) =>
            m.par_ron_id === activeMatchup.ron_id &&
            m.par_equ_1_id === activeMatchup.equ_1_id &&
            m.par_equ_2_id === activeMatchup.equ_2_id
        );
        if (activeMatchupMatches.length > 0) {
          const maxBoard = Math.max(...activeMatchupMatches.map((m) => m.tablero));
          setSelectedBoard(maxBoard + 1);
        } else {
          setSelectedBoard(1);
        }
      }
    } else {
      setSelectedBoard(1);
    }
  }, [selectedMatchupKey, matches, matchups]);

  // Clear board inputs on matchup change
  useEffect(() => {
    setSelectedWhite('');
    setSelectedBlack('');
    setSelectedResult('');
  }, [selectedMatchupKey]);

  // Round Handlers
  const handleAddRound = () => {
    const nextRoundNum = rounds.length + 1;
    if (nextRoundNum > nroRondas) {
      const confirmModify = window.confirm(
        `La ronda agregada excede el número de rondas configurado para el torneo (${nroRondas}). ¿Deseas continuar y modificar el total de rondas del torneo?`
      );
      if (!confirmModify) return;

      onUpdateTournamentRondas(nextRoundNum);
    }

    const newRound = {
      ron_id: nextRoundNum,
      ron_numero: nextRoundNum,
      ron_fecha: '',
    };
    setRounds([...rounds, newRound]);
    setActiveRoundId(newRound.ron_id);
  };

  const handleRoundDateChange = (ronId, date) => {
    setRounds((prev) =>
      prev.map((r) => (r.ron_id === ronId ? { ...r, ron_fecha: date } : r))
    );
  };

  // Matchup Handlers
  const handleAddMatchup = () => {
    if (!selectedEqu1) {
      setAlertInfo({ open: true, message: 'Debes seleccionar el Equipo 1.', severity: 'warning' });
      return;
    }
    if (!selectedEqu2) {
      setAlertInfo({ open: true, message: 'Debes seleccionar el Equipo 2 o BYE.', severity: 'warning' });
      return;
    }
    if (selectedEqu1 === selectedEqu2) {
      setAlertInfo({ open: true, message: 'Un equipo no puede jugar contra sí mismo.', severity: 'warning' });
      return;
    }

    // Check if team 1 already has a matchup in this round
    const equ1Played = matchups.some(
      (m) => m.ron_id === activeRoundId && (m.equ_1_id === selectedEqu1 || m.equ_2_id === selectedEqu1)
    );
    if (equ1Played) {
      const t = teams.find((x) => x.id === selectedEqu1);
      setAlertInfo({ open: true, message: `El equipo "${t?.nombre}" ya tiene un enfrentamiento asignado en esta ronda.`, severity: 'warning' });
      return;
    }

    // Check if team 2 already has a matchup in this round (if not BYE)
    if (selectedEqu2 !== 'BYE') {
      const equ2Played = matchups.some(
        (m) => m.ron_id === activeRoundId && (m.equ_1_id === selectedEqu2 || m.equ_2_id === selectedEqu2)
      );
      if (equ2Played) {
        const t = teams.find((x) => x.id === selectedEqu2);
        setAlertInfo({ open: true, message: `El equipo "${t?.nombre}" ya tiene un enfrentamiento asignado en esta ronda.`, severity: 'warning' });
        return;
      }
    }

    const eq2Id = selectedEqu2 === 'BYE' ? 0 : selectedEqu2;
    const newKey = `${activeRoundId}-${selectedEqu1}-${eq2Id}`;
    const newMatchup = {
      ron_id: activeRoundId,
      equ_1_id: selectedEqu1,
      equ_2_id: eq2Id,
      key: newKey,
    };

    setMatchups([...matchups, newMatchup]);
    setSelectedMatchupKey(newKey);
    setSelectedEqu1('');
    setSelectedEqu2('');
  };

  const handleDeleteMatchup = (keyToDelete) => {
    const matchup = matchups.find((m) => m.key === keyToDelete);
    if (!matchup) return;

    // Remove matchup and all its board matches
    setMatchups((prev) => prev.filter((m) => m.key !== keyToDelete));
    setMatches((prev) =>
      prev.filter(
        (m) =>
          !(
            m.par_ron_id === matchup.ron_id &&
            m.par_equ_1_id === matchup.equ_1_id &&
            m.par_equ_2_id === matchup.equ_2_id
          )
      )
    );

    if (selectedMatchupKey === keyToDelete) {
      setSelectedMatchupKey(null);
    }
  };

  // Board Game Handlers
  const handleAddBoardMatch = () => {
    if (!selectedMatchupKey) return;
    const activeMatchup = matchups.find((m) => m.key === selectedMatchupKey);
    if (!activeMatchup || activeMatchup.equ_2_id === 0) return; // Cannot add games if opponent is BYE

    if (!selectedBoard || selectedBoard <= 0) {
      setAlertInfo({ open: true, message: 'Ingresa un número de tablero válido.', severity: 'warning' });
      return;
    }
    if (!selectedWhite) {
      setAlertInfo({ open: true, message: 'Selecciona el jugador de Blancas.', severity: 'warning' });
      return;
    }
    if (!selectedBlack) {
      setAlertInfo({ open: true, message: 'Selecciona el jugador de Negras.', severity: 'warning' });
      return;
    }
    if (!selectedResult) {
      setAlertInfo({ open: true, message: 'Selecciona el resultado.', severity: 'warning' });
      return;
    }

    const activeMatchupMatches = matches.filter(
      (m) =>
        m.par_ron_id === activeMatchup.ron_id &&
        m.par_equ_1_id === activeMatchup.equ_1_id &&
        m.par_equ_2_id === activeMatchup.equ_2_id
    );

    // Board uniqueness
    const boardExists = activeMatchupMatches.some((m) => m.tablero === Number(selectedBoard));
    if (boardExists) {
      setAlertInfo({ open: true, message: `El tablero ${selectedBoard} ya está registrado para este enfrentamiento.`, severity: 'warning' });
      return;
    }

    // Player duplicate check in this round
    const whitePlayed = matches.some(
      (m) =>
        m.par_ron_id === activeMatchup.ron_id &&
        (m.jug_blancas === selectedWhite || m.jug_negras === selectedWhite)
    );
    if (whitePlayed) {
      setAlertInfo({ open: true, message: 'El jugador de blancas ya tiene una partida asignada en esta ronda.', severity: 'warning' });
      return;
    }

    const blackPlayed = matches.some(
      (m) =>
        m.par_ron_id === activeMatchup.ron_id &&
        (m.jug_blancas === selectedBlack || m.jug_negras === selectedBlack)
    );
    if (blackPlayed) {
      setAlertInfo({ open: true, message: 'El jugador de negras ya tiene una partida asignada en esta ronda.', severity: 'warning' });
      return;
    }

    const newMatch = {
      id: Date.now(),
      par_ron_id: activeMatchup.ron_id,
      par_equ_1_id: activeMatchup.equ_1_id,
      par_equ_2_id: activeMatchup.equ_2_id,
      tablero: Number(selectedBoard),
      jug_blancas: selectedWhite,
      jug_negras: selectedBlack,
      result: selectedResult,
    };

    setMatches([...matches, newMatch]);

    // Reset board form & increment board number
    setSelectedWhite('');
    setSelectedBlack('');
    setSelectedResult('');
    setSelectedBoard((prev) => Number(prev) + 1);
  };

  const handleDeleteBoardMatch = (matchId) => {
    setMatches((prev) => prev.filter((m) => m.id !== matchId));
  };

  // Helper getters
  const getTeamName = (teamId) => {
    if (teamId === 0) return 'BYE';
    const team = teams.find((t) => t.id === teamId);
    return team ? team.nombre : `Equipo ID: ${teamId}`;
  };

  const getPlayerName = (teamId, jugId) => {
    const team = teams.find((t) => t.id === teamId);
    if (!team) return `Jugador ID: ${jugId}`;
    const player = team.jugadores.find((p) => p.jug_id === jugId);
    return player ? player.apellido_nombre : `Jugador ID: ${jugId}`;
  };

  const getTeamPlayers = (teamId) => {
    const team = teams.find((t) => t.id === teamId);
    if (!team) return [];
    return [...team.jugadores].sort((a, b) =>
      a.apellido_nombre.localeCompare(b.apellido_nombre)
    );
  };

  const handleSaveAndNext = async () => {
    // 1. Dirty check
    const roundsChanged = !initialRounds || rounds.length !== initialRounds.length || rounds.some((r, idx) => {
      const initR = initialRounds[idx];
      return !initR || r.ron_id !== initR.ron_id || r.ron_numero !== initR.ron_numero || (r.ron_fecha || '') !== (initR.ron_fecha || '');
    });

    const matchupsChanged = !initialMatchups || matchups.length !== initialMatchups.length || matchups.some((m, idx) => {
      const initM = initialMatchups[idx];
      return !initM || m.ron_id !== initM.ron_id || m.equ_1_id !== initM.equ_1_id || m.equ_2_id !== initM.equ_2_id;
    });

    const matchesChanged = !initialMatches || matches.length !== initialMatches.length || matches.some((m, idx) => {
      const initM = initialMatches[idx];
      return !initM ||
             m.par_ron_id !== initM.par_ron_id ||
             m.par_equ_1_id !== initM.par_equ_1_id ||
             m.par_equ_2_id !== initM.par_equ_2_id ||
             m.tablero !== initM.tablero ||
             m.jug_blancas !== initM.jug_blancas ||
             m.jug_negras !== initM.jug_negras ||
             m.result !== initM.result;
    });

    const isDirty = roundsChanged || matchupsChanged || matchesChanged;

    if (!isDirty) {
      onNext();
      return;
    }

    // 2. Rond count check
    if (rounds.length !== nroRondas) {
      const confirmRoundsCount = window.confirm(
        `La cantidad de rondas configuradas (${rounds.length}) no coincide con la cantidad declarada en los datos generales del torneo (${nroRondas}). ¿Deseas guardar de todos modos?`
      );
      if (!confirmRoundsCount) return;
    }

    // 3. Matchup validation: warn if any matchup has 0 games and is not a BYE
    const incompleteMatchups = [];
    for (const matchup of matchups) {
      if (matchup.equ_2_id === 0) continue; // BYE matchup is fine with 0 games
      const boardCount = matches.filter(
        (m) =>
          m.par_ron_id === matchup.ron_id &&
          m.par_equ_1_id === matchup.equ_1_id &&
          m.par_equ_2_id === matchup.equ_2_id
      ).length;

      if (boardCount === 0) {
        incompleteMatchups.push(`${getTeamName(matchup.equ_1_id)} vs ${getTeamName(matchup.equ_2_id)} (Ronda ${matchup.ron_id})`);
      }
    }

    if (incompleteMatchups.length > 0) {
      const list = incompleteMatchups.join('\n');
      const confirmIncomplete = window.confirm(
        `Los siguientes enfrentamientos no tienen partidas registradas:\n${list}\n\n¿Deseas guardar de todos modos y cargarlas más adelante?`
      );
      if (!confirmIncomplete) return;
    }

    // 4. Construct payload
    const dtoRondas = rounds.map((r) => ({
      ron_id: r.ron_id,
      ron_numero: r.ron_numero,
      ron_fecha: r.ron_fecha || undefined,
    }));

    const dtoEnfrentamientos = matchups.map((m) => ({
      ron_id: m.ron_id,
      equ_1_id: m.equ_1_id,
      equ_2_id: m.equ_2_id,
    }));

    const dtoPartidas = matches.map((m) => {
      const {
        par_resultado_blancas,
        par_tipo_res_blancas,
        par_resultado_negras,
        par_tipo_res_negras,
      } = serializeMatchResult(m.result);

      return {
        par_ron_id: m.par_ron_id,
        par_equ_1_id: m.par_equ_1_id,
        par_equ_2_id: m.par_equ_2_id,
        par_tablero: m.tablero, // Save board number in par_tablero field
        par_jug_blancas: Number(m.jug_blancas),
        par_jug_negras: Number(m.jug_negras),
        par_resultado_blancas,
        par_tipo_res_blancas,
        par_resultado_negras,
        par_tipo_res_negras,
      };
    });

    setLoading(true);
    try {
      await saveTeamsRoundsMatches(torId, {
        rondas: dtoRondas,
        enfrentamientos: dtoEnfrentamientos,
        partidas: dtoPartidas,
      });
      setAlertInfo({
        open: true,
        message: 'Enfrentamientos y partidas por equipos guardados correctamente.',
        severity: 'success',
      });
      onNext();
    } catch (error) {
      console.error('Error saving team rounds/matches:', error);
      setAlertInfo({
        open: true,
        message: error.message || 'Error al guardar los enfrentamientos y partidas.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const activeRound = rounds.find((r) => r.ron_id === activeRoundId) || rounds[0];
  const activeRoundMatchups = matchups.filter((m) => m.ron_id === activeRoundId);

  return (
    <Box>
      <Typography variant="h6" sx={{ color: '#cbd5e1', mb: 2, fontWeight: 600 }}>
        Paso 3: Enfrentamientos y Partidas por Equipos
      </Typography>

      {dataLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {/* Column 1: Rounds List */}
          <Grid item xs={12} md={3}>
            <RoundsPanel
              rounds={rounds}
              activeRoundId={activeRoundId}
              onRoundSelect={(ronId) => {
                setActiveRoundId(ronId);
                const matchInRound = matchups.find((m) => m.ron_id === ronId);
                setSelectedMatchupKey(matchInRound ? matchInRound.key : null);
              }}
              onRoundDateChange={handleRoundDateChange}
              onAddRound={handleAddRound}
              minHeight="400px"
              maxListHeight="270px"
              addLabel="+ Ronda"
            />
          </Grid>

          {/* Column 2: Team Matchups (75% width) */}
          <Grid item xs={12} md={9}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                background: 'rgba(15, 23, 42, 0.5)',
                borderColor: 'rgba(255, 255, 255, 0.08)',
                borderRadius: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
                minHeight: '400px',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: '#cbd5e1', fontWeight: 600 }}>
                Enfrentamientos y Partidas por Equipos - Ronda {activeRound?.ron_numero}
              </Typography>

              {/* Form to Add Pair */}
              <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', pb: 2.5 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Equipo 1"
                      value={selectedEqu1}
                      onChange={(e) => setSelectedEqu1(e.target.value)}
                      InputLabelProps={{ style: { color: '#94a3b8' } }}
                      SelectProps={{ style: { color: '#cbd5e1' }, MenuProps: selectMenuProps }}
                      sx={compactInputSx}
                    >
                      <MenuItem value=""><em>Selecciona...</em></MenuItem>
                      {teams.map((t) => (
                        <MenuItem key={t.id} value={t.id}>{t.nombre}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Equipo 2 / BYE"
                      value={selectedEqu2}
                      onChange={(e) => setSelectedEqu2(e.target.value)}
                      InputLabelProps={{ style: { color: '#94a3b8' } }}
                      SelectProps={{ style: { color: '#cbd5e1' }, MenuProps: selectMenuProps }}
                      sx={compactInputSx}
                    >
                      <MenuItem value=""><em>Selecciona...</em></MenuItem>
                      <MenuItem value="BYE" sx={{ fontWeight: 'bold', color: '#3a6eb6' }}>BYE</MenuItem>
                      {teams.map((t) => (
                        <MenuItem key={t.id} value={t.id}>{t.nombre}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleAddMatchup}
                      sx={{
                        height: '34px',
                        background: '#3a6eb6',
                        borderRadius: 0,
                        fontSize: '0.8rem',
                        '&:hover': { background: '#2a5793' },
                      }}
                    >
                      Agregar Enfrentamiento
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* Matchups list */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: '650px', overflowY: 'auto', pr: 0.5, flexGrow: 1 }}>
                {activeRoundMatchups.length === 0 ? (
                  <Typography variant="caption" sx={{ color: '#64748b', textAlign: 'center', my: 4 }}>
                    No hay enfrentamientos en esta ronda.
                  </Typography>
                ) : (
                  activeRoundMatchups.map((m) => {
                    const isSelected = selectedMatchupKey === m.key;
                    const gameCount = matches.filter(
                      (x) =>
                        x.par_ron_id === m.ron_id &&
                        x.par_equ_1_id === m.equ_1_id &&
                        x.par_equ_2_id === m.equ_2_id
                    ).length;

                    const matchGames = matches
                      .filter(
                        (x) =>
                          x.par_ron_id === m.ron_id &&
                          x.par_equ_1_id === m.equ_1_id &&
                          x.par_equ_2_id === m.equ_2_id
                      )
                      .sort((a, b) => a.tablero - b.tablero);

                    const isBye = m.equ_2_id === 0;

                    return (
                      <Paper
                        variant="outlined"
                        key={m.key}
                        sx={{
                          background: 'rgba(30, 41, 59, 0.2)',
                          borderColor: isSelected ? '#3a6eb6' : 'rgba(255, 255, 255, 0.05)',
                          borderRadius: 0,
                          overflow: 'hidden',
                          transition: 'border-color 0.2s',
                        }}
                      >
                        {/* Matchup Header */}
                        <Box
                          onClick={() => setSelectedMatchupKey(isSelected ? null : m.key)}
                          sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: isSelected ? 'rgba(58, 110, 182, 0.12)' : 'rgba(255, 255, 255, 0.01)',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              background: isSelected ? 'rgba(58, 110, 182, 0.18)' : 'rgba(255, 255, 255, 0.03)',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flexGrow: 1, mr: 2 }}>
                            {isBye ? null : (
                              isSelected ? <KeyboardArrowDownIcon sx={{ color: '#94a3b8' }} /> : <KeyboardArrowRightIcon sx={{ color: '#94a3b8' }} />
                            )}
                            <Typography variant="body2" noWrap sx={{ color: '#cbd5e1', fontWeight: 600, fontSize: '0.85rem' }}>
                              {isBye ? getTeamName(m.equ_1_id) : `${getTeamName(m.equ_1_id)} vs ${getTeamName(m.equ_2_id)}`}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                            {m.equ_2_id !== 0 && (
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 0.25,
                                  background: isSelected ? 'rgba(58, 110, 182, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                                  borderRadius: '2px',
                                  border: '1px solid rgba(255, 255, 255, 0.05)',
                                }}
                              >
                                <Typography variant="caption" sx={{ color: isSelected ? '#60a5fa' : '#94a3b8', fontSize: '0.75rem', fontWeight: 500 }}>
                                  {gameCount} {gameCount === 1 ? 'partida' : 'partidas'}
                                </Typography>
                              </Box>
                            )}
                            {m.equ_2_id === 0 && (
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 0.25,
                                  background: 'rgba(234, 179, 8, 0.1)',
                                  borderRadius: '2px',
                                  border: '1px solid rgba(234, 179, 8, 0.2)',
                                }}
                              >
                                <Typography variant="caption" sx={{ color: '#eab308', fontSize: '0.75rem', fontWeight: 600 }}>
                                  BYE
                                </Typography>
                              </Box>
                            )}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMatchup(m.key);
                              }}
                              sx={{ color: '#ef4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        {/* Collapsible content (nested table & form) */}
                        <Collapse in={isSelected} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.05)', background: 'rgba(15, 23, 42, 0.3)' }}>
                            {isBye ? (
                              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', py: 1 }}>
                                El equipo {getTeamName(m.equ_1_id)} tiene un BYE esta ronda. No se cargan tableros individuales.
                              </Typography>
                            ) : (
                              <>
                                <Typography variant="subtitle2" sx={{ color: '#94a3b8', fontSize: '0.8rem', fontWeight: 600, mb: 1.5 }}>
                                  Partidas por Tableros
                                </Typography>

                                {matchGames.length === 0 ? (
                                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block', textAlign: 'center', my: 2.5 }}>
                                    No hay tableros registrados para este enfrentamiento.
                                  </Typography>
                                ) : (
                                  <TableContainer sx={{ border: '1px solid rgba(255, 255, 255, 0.05)', overflowX: 'auto', mb: 2.5 }}>
                                    <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 600 }}>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell sx={{ py: 0.75, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', fontSize: '0.75rem', borderColor: 'rgba(255, 255, 255, 0.08)', width: '10%' }}>Tab.</TableCell>
                                          <TableCell sx={{ py: 0.75, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', fontSize: '0.75rem', borderColor: 'rgba(255, 255, 255, 0.08)', width: '38%' }}>Blancas</TableCell>
                                          <TableCell align="center" sx={{ py: 0.75, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', fontSize: '0.75rem', borderColor: 'rgba(255, 255, 255, 0.08)', width: '14%' }}>Res.</TableCell>
                                          <TableCell sx={{ py: 0.75, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', fontSize: '0.75rem', borderColor: 'rgba(255, 255, 255, 0.08)', width: '38%' }}>Negras</TableCell>
                                          <TableCell align="right" sx={{ py: 0.75, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', fontSize: '0.75rem', borderColor: 'rgba(255, 255, 255, 0.08)', width: '10%' }}></TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {matchGames.map((game) => (
                                          <TableRow key={game.id} hover sx={{ '&:hover': { background: 'rgba(255, 255, 255, 0.02) !important' } }}>
                                            <TableCell sx={{ py: 0.75, color: '#94a3b8', fontSize: '0.75rem', borderColor: 'rgba(255, 255, 255, 0.05)' }}>{game.tablero}</TableCell>
                                            <TableCell sx={{ py: 0.75, color: '#cbd5e1', fontSize: '0.75rem', borderColor: 'rgba(255, 255, 255, 0.05)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                              {getPlayerName(m.equ_1_id, game.jug_blancas)}
                                            </TableCell>
                                            <TableCell align="center" sx={{ py: 0.75, color: '#3a6eb6', fontWeight: 'bold', fontFamily: 'Lucida Console, monospace', fontSize: '0.75rem', borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                                              {game.result}
                                            </TableCell>
                                            <TableCell sx={{ py: 0.75, color: '#cbd5e1', fontSize: '0.75rem', borderColor: 'rgba(255, 255, 255, 0.05)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                              {getPlayerName(m.equ_2_id, game.jug_negras)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 0.75, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                                              <IconButton
                                                size="small"
                                                onClick={() => handleDeleteBoardMatch(game.id)}
                                                sx={{ p: 0.25, color: '#ef4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                                              >
                                                <DeleteIcon fontSize="small" />
                                              </IconButton>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                )}

                                {/* Compact inline form to add a board game */}
                                <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.05)', pt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ color: '#cbd5e1', fontSize: '0.8rem', fontWeight: 600, mb: 1.5 }}>
                                    Cargar Nuevo Tablero
                                  </Typography>
                                  <Grid container spacing={1.5} alignItems="center">
                                    <Grid item xs={3} sm={1.5}>
                                      <TextField
                                        type="number"
                                        size="small"
                                        label="Tablero"
                                        value={selectedBoard}
                                        onChange={(e) => setSelectedBoard(e.target.value)}
                                        InputLabelProps={{ style: { color: '#94a3b8' } }}
                                        inputProps={{ min: 1, style: { color: '#cbd5e1' } }}
                                        sx={compactInputSx}
                                      />
                                    </Grid>
                                    <Grid item xs={9} sm={4}>
                                      <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Blancas (Eq. 1)"
                                        value={selectedWhite}
                                        onChange={(e) => setSelectedWhite(e.target.value)}
                                        InputLabelProps={{ style: { color: '#94a3b8' } }}
                                        SelectProps={{ style: { color: '#cbd5e1' }, MenuProps: selectMenuProps }}
                                        sx={compactInputSx}
                                      >
                                        <MenuItem value=""><em>Selecciona...</em></MenuItem>
                                        {getTeamPlayers(m.equ_1_id).map((p) => (
                                          <MenuItem key={p.jug_id} value={p.jug_id}>{p.apellido_nombre}</MenuItem>
                                        ))}
                                      </TextField>
                                    </Grid>
                                    <Grid item xs={3} sm={1.5}>
                                      <MatchResultSelect
                                        value={selectedResult}
                                        onChange={(e) => setSelectedResult(e.target.value)}
                                        label="Res."
                                        placeholder="..."
                                      />
                                    </Grid>
                                    <Grid item xs={9} sm={4}>
                                      <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Negras (Eq. 2)"
                                        value={selectedBlack}
                                        onChange={(e) => setSelectedBlack(e.target.value)}
                                        InputLabelProps={{ style: { color: '#94a3b8' } }}
                                        SelectProps={{ style: { color: '#cbd5e1' }, MenuProps: selectMenuProps }}
                                        sx={compactInputSx}
                                      >
                                        <MenuItem value=""><em>Selecciona...</em></MenuItem>
                                        {getTeamPlayers(m.equ_2_id).map((p) => (
                                          <MenuItem key={p.jug_id} value={p.jug_id}>{p.apellido_nombre}</MenuItem>
                                        ))}
                                      </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={1}>
                                      <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={handleAddBoardMatch}
                                        sx={{
                                          height: '34px',
                                          background: '#3a6eb6',
                                          borderRadius: 0,
                                          fontSize: '0.8rem',
                                          fontWeight: 'bold',
                                          '&:hover': { background: '#2a5793' },
                                        }}
                                      >
                                        +
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </>
                            )}
                          </Box>
                        </Collapse>
                      </Paper>
                    );
                  })
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Navigation Buttons */}
      <StepNavButtons
        onBack={onBack}
        onNext={handleSaveAndNext}
        loading={loading}
      />
    </Box>
  );
}
